import { useReveal } from "@/hooks/useReveal";

const STAGES = [
  {
    n: "01",
    title: "Diamond Selection",
    text: "Only GIA-certified stones above an exacting threshold are admitted into the maison's vault.",
    img: "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/8i5dnjtv_C4C4691B-C6A8-4448-BFBA-962B917AFDFE.png",
  },
  {
    n: "02",
    title: "Movement Assembly",
    text: "Each PD Calibre is assembled by a single watchmaker — never shared, never rushed.",
    img: "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/vehdzfru_BA376CB4-99C2-4D3B-8E04-D390C34C1D23.png",
  },
  {
    n: "03",
    title: "Hand Finishing",
    text: "Côtes, anglage, and mirror-polish performed by hand over weeks of quiet labour.",
    img: "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/7bpzcfrn_9A477286-2930-40BC-A0B0-815E05268C8E.png",
  },
  {
    n: "04",
    title: "Watchmaker Inspection",
    text: "A final, uncompromising review — to confirm the piece is ready to bear our name.",
    img: "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/8fq4tf57_390F8100-5E7A-4BA5-A9B5-20819DF67749.png",
  },
  {
    n: "05",
    title: "Presentation Box",
    text: "Hand-finished noir lacquer vitrine, lined in velvet — a stage for a single legacy.",
    img: "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/xxvkq5an_2BCC1627-4054-4FA9-B6B6-96B9DF560544.png",
  },
];

export default function CraftsmanshipSection() {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();

  return (
    <section
      id="craftsmanship"
      data-testid="craftsmanship-section"
      className="relative bg-[#050505] py-28 md:py-40 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-20">
          <div className="lg:col-span-7">
            <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="craft-eyebrow">The Hands Behind the Maison</p>
            <h2
              ref={title}
              className="pd-reveal mt-5 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.06em] leading-[1.05]"
              style={{ transitionDelay: "160ms" }}
              data-testid="craft-title"
            >
              Crafted Without <br className="hidden md:block" />
              <span className="italic font-serif-body text-white/85">Compromise.</span>
            </h2>
          </div>
          <div ref={lead} className="pd-reveal lg:col-span-5" style={{ transitionDelay: "260ms" }}>
            <div className="hairline mb-6" />
            <p className="font-light text-white/65 text-base md:text-lg leading-relaxed tracking-wide">
              From the first stone to the closing of the vitrine, every piece passes through
              five disciplines — and a single, unrelenting standard. Time, here, is not measured.
              It is invested.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-px bg-[var(--pd-border)]">
          {STAGES.map((s, i) => (
            <Stage key={s.n} stage={s} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Stage({ stage, index }) {
  const r = useReveal();
  return (
    <div
      ref={r}
      data-testid={`craft-stage-${stage.n}`}
      className="pd-reveal relative bg-black group overflow-hidden"
      style={{ transitionDelay: `${index * 140}ms` }}
    >
      <div className="aspect-[3/4] overflow-hidden">
        <img
          src={stage.img}
          alt={stage.title}
          className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      </div>
      <div className="absolute inset-0 p-6 flex flex-col justify-between">
        <span className="font-serif-display text-2xl text-[var(--pd-gold)] tracking-widest">{stage.n}</span>
        <div>
          <h3 className="font-serif-display text-xl md:text-2xl text-white tracking-[0.1em]">{stage.title}</h3>
          <p className="mt-2 text-white/65 text-xs leading-relaxed tracking-wide">{stage.text}</p>
        </div>
      </div>
    </div>
  );
}
